import React from "react";
import { Link } from "react-router-dom";
import PublicLayout, { INDOCG_LOGO_URL } from "./PublicLayout";
import { ArrowRight, Award, ShieldCheck, Factory, Cog } from "lucide-react";

const CERTS = [
  { name: "ISO 9001:2015", desc: "Quality Management" },
  { name: "MSME", desc: "Registered Enterprise" },
  { name: "BIS Compliant", desc: "Bureau of Indian Standards" },
  { name: "UDYAM", desc: "Government of India Registration" },
];

const TIMELINE = [
  { year: "2011", event: "INDOCG Group founded in Prayagraj, UP." },
  { year: "2015", event: "Indus Hoses Pvt. Ltd. incorporated as the hydraulic-assembly arm." },
  { year: "2018", event: "ISO 9001:2015 certification. First OEM supply contract signed." },
  { year: "2021", event: "Expanded crimping capacity to serve open-cast mining fleets across Central India." },
  { year: "2024", event: "Launched INDOCG Nexus — an internal digital platform for order acknowledgment, decoding and dispatch tracking." },
  { year: "2026", event: "Now serving 200+ industrial customers with 48-hour dispatch across UP, Jharkhand, Chhattisgarh, MP and Odisha." },
];

const NUMBERS = [
  { v: "15+", l: "Years in hydraulics" },
  { v: "200+", l: "Industrial customers" },
  { v: "10,000+", l: "Assemblies delivered / yr" },
  { v: "48hr", l: "Dispatch on standard SKUs" },
];

export default function About() {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="border-b border-white/10" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— About IHPL</div>
            <h1 className="font-industrial uppercase tracking-tighter text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-white" data-testid="about-h1">
              Fifteen years<br /> of <span className="text-[#E11D2A]">high pressure.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 space-y-5 text-white/70 text-lg leading-relaxed">
            <p>
              Indus Hoses is the hydraulic-assembly arm of the <span className="text-white font-medium">INDOCG Group</span>, headquartered in Prayagraj, Uttar Pradesh.
            </p>
            <p>
              We build hydraulic hose assemblies — that's all we do. No side hustles, no distractions. One product, done extremely well.
            </p>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="border-b border-white/10 bg-[#080808]" data-testid="about-numbers">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {NUMBERS.map((n, i) => (
              <div key={n.l} className="border-l-2 border-[#E11D2A] pl-6" data-testid={`about-stat-${i}`}>
                <div className="font-industrial text-6xl md:text-7xl text-white leading-none">{n.v}</div>
                <div className="mt-3 font-tech text-[11px] uppercase tracking-[0.25em] text-white/50">{n.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="about-timeline">
        <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— Timeline</div>
        <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-6xl text-white leading-none mb-14">The story so far.</h2>

        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
          <div className="space-y-16">
            {TIMELINE.map((t, i) => (
              <div key={t.year} className={`relative grid md:grid-cols-2 gap-8 ${i % 2 === 0 ? "" : "md:direction-rtl"}`} data-testid={`timeline-${i}`}>
                <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16 md:col-start-2"}`}>
                  <div className="font-industrial text-5xl md:text-6xl text-[#E11D2A] leading-none mb-3">{t.year}</div>
                  <p className="text-white/70 text-lg leading-relaxed">{t.event}</p>
                </div>
                <div className="absolute left-4 md:left-1/2 top-3 -translate-x-1/2 w-3 h-3 bg-[#E11D2A]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-y border-white/10 bg-[#080808]" data-testid="about-capabilities">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— Manufacturing</div>
            <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-6xl text-white leading-none">In-house from cut to crimp.</h2>
            <p className="mt-8 text-white/70 text-lg leading-relaxed">
              Every step of the assembly happens under our roof — cutting, skiving, crimping, marking, and pressure testing — on calibrated machines with certified operators.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-3">
              {[
                { icon: Factory, l: "Prayagraj Facility" },
                { icon: Cog, l: "Calibrated Crimpers" },
                { icon: ShieldCheck, l: "Hydrostatic Test Bay" },
                { icon: Award, l: "Certified QC Team" },
              ].map(({ icon: Icon, l }) => (
                <div key={l} className="flex items-center gap-3 border border-white/10 px-4 py-3">
                  <Icon size={18} className="text-[#E11D2A]" />
                  <span className="font-tech text-xs uppercase tracking-[0.2em] text-white/80">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img src="https://images.pexels.com/photos/36237165/pexels-photo-36237165.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="Hose assembly" className="w-full h-full object-cover" />
            <div className="absolute inset-0 border border-white/10" />
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="about-certs">
        <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#E11D2A] mb-6">— Certifications</div>
        <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-6xl text-white leading-none mb-14">Documented quality.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CERTS.map((c, i) => (
            <div key={c.name} className="p-8 border border-white/10 hover:border-[#E11D2A] transition-colors" data-testid={`cert-block-${i}`}>
              <Award size={28} className="text-[#E11D2A] mb-6" strokeWidth={1.5} />
              <div className="font-industrial uppercase text-2xl text-white tracking-tight leading-none">{c.name}</div>
              <div className="mt-3 font-tech text-[11px] uppercase tracking-[0.25em] text-white/50">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Parent group */}
      <section className="border-t border-white/10 bg-[#080808] py-20" data-testid="about-indocg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">— Part of</div>
            <div className="flex items-center gap-6 mb-8">
              <img
                src={INDOCG_LOGO_URL}
                alt="INDOCG Group"
                className="h-24 md:h-28 w-auto object-contain"
                data-testid="indocg-logo-about"
              />
              <div className="leading-none">
                <div className="font-industrial text-white text-5xl md:text-6xl uppercase tracking-tight">INDOCG</div>
                <div className="font-industrial text-white/70 text-3xl md:text-4xl uppercase tracking-tight mt-2">Group</div>
              </div>
            </div>
            <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
              Fifteen years<br/> of <span className="text-[#E11D2A]">quality.</span>
            </h2>
          </div>
          <div className="text-white/70 text-lg leading-relaxed space-y-4">
            <p>
              Founded in Prayagraj in 2011, the INDOCG Group is a diversified industrial holding with interests in hydraulics, general trading and engineering services.
            </p>
            <p>
              Indus Hoses Pvt. Ltd. carries forward the group's 15-year heritage of quality, integrity, and long-term customer partnerships.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="about-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-industrial uppercase tracking-tight text-4xl md:text-5xl text-white leading-none">
            Work with us.<br /><span className="text-[#E11D2A]">Every hose accountable.</span>
          </h2>
          <Link to="/contact" className="inline-flex items-center gap-3 px-7 py-4 bg-[#E11D2A] hover:bg-[#C81622] transition-colors font-tech text-xs uppercase tracking-[0.25em]" data-testid="about-cta-link">
            Get in Touch <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
